import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, Download } from "lucide-react";

export default function Report() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/projects/${id}`).then(({ data }) => setP(data)); }, [id]);
  if (!p) return null;
  const spec = p.specification || {};
  const intent = p.intent || {};
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/report.pdf`;
  const nd = (v) => v && String(v).trim() ? v : "Not provided";
  const fmt = (v) => String(v || "").replace(/_/g, " ") || "not started";
  const generated = new Date().toISOString().slice(0, 19).replace("T", " ") + " UTC";

  return (
    <div className="space-y-8" data-testid="report-view">
      <div className="no-print flex justify-between items-center">
        <div>
          <div className="font-tech mb-2">Project Summary Report</div>
          <h1 className="text-2xl font-semibold text-white">Printable View</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={() => window.print()} className="btn-ghost" data-testid="report-print"><Printer className="w-4 h-4" /> Print / Save as PDF</button>
          <a href={pdfUrl} className="btn-primary" data-testid="report-download"><Download className="w-4 h-4" /> Download PDF</a>
        </div>
      </div>

      <article className="panel-elev p-8 lg:p-12 bg-white/95 text-slate-900 print:bg-white print:text-black">
        <header className="border-b border-slate-300 pb-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-[#0066FF]">A.I.U.D.E. — AI-Integrated Universal Design Engine</div>
              <h1 className="text-3xl font-bold mt-2">Project Summary Report</h1>
              <div className="mt-1 text-lg">{nd(intent.project_name)}</div>
            </div>
            <div className="text-right text-xs font-mono">
              <div>ID · {p.id.slice(0, 12)}</div>
              <div>Revision · {p.revision}</div>
              <div>Generated · {generated}</div>
            </div>
          </div>
        </header>

        <section className="mb-6">
          <h2 className="text-lg font-bold text-[#0066FF] border-b border-slate-200 pb-1 mb-3">Project Intent</h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {Object.entries({
              "Description": intent.description, "Intended use": intent.intended_use, "Primary user": intent.primary_user,
              "Operating environment": intent.operating_environment, "Required load / performance": intent.required_load,
              "Approximate dimensions": intent.approximate_dimensions, "Preferred material": intent.preferred_material,
              "Manufacturing method": intent.manufacturing_method, "Budget priority": intent.budget_priority,
              "Additional constraints": intent.additional_constraints,
            }).map(([k, v]) => (
              <div key={k}><dt className="font-semibold">{k}</dt><dd className="text-slate-700">{nd(v)}</dd></div>
            ))}
          </dl>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-bold text-[#0066FF] border-b border-slate-200 pb-1 mb-3">Structured Specification</h2>
          {["functional_objective", "dimensional_requirements", "material_requirements", "loading_conditions", "environmental_conditions", "manufacturing_constraints", "safety_considerations"].map((k) => (
            <div key={k} className="mb-3">
              <div className="font-semibold text-sm capitalize">{k.replace(/_/g, " ")}</div>
              <div className="text-sm whitespace-pre-wrap">{nd(spec[k])}</div>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div>
              <div className="font-semibold text-sm">Missing information</div>
              <ul className="list-disc ml-5 text-sm">{(spec.missing_information || ["None recorded"]).map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
            <div>
              <div className="font-semibold text-sm">Assumptions requiring confirmation</div>
              <ul className="list-disc ml-5 text-sm">{(spec.assumptions_requiring_confirmation || ["None recorded"]).map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-bold text-[#0066FF] border-b border-slate-200 pb-1 mb-3">Current Workflow Stage</h2>
          <table className="w-full text-sm border border-slate-300">
            <tbody>
              {[
                ["Status", fmt(p.status)], ["Specification approved", p.specification_approved ? "Yes" : "No"],
                ["Geometry", fmt(p.geometry_status)], ["Material selection", fmt(p.material_status)],
                ["Simulation", fmt(p.simulation_status)], ["Validation", fmt(p.validation_status)],
                ["Manufacturing outputs", fmt(p.manufacturing_output_status)],
              ].map(([k, v]) => (
                <tr key={k}><td className="border border-slate-300 px-3 py-1 font-semibold w-1/3">{k}</td><td className="border border-slate-300 px-3 py-1 capitalize">{v}</td></tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="text-xs text-slate-600 border-t border-slate-300 pt-4">
          <p><strong>Disclaimer.</strong> A.I.U.D.E. is a business demonstration and project-intake platform. It does not perform production-grade CAD, FEA, CFD, certification, or autonomous manufacturing. Professional engineering review is required before real-world fabrication or safety-critical use.</p>
          <p className="mt-2">Talons Inclusion Factory — San Pedro, CA — TalonsInclusionFactory.com</p>
          <p>IP Moat Locked — All Rights Reserved — Tallan J. Acalin, Sole Executive Officer</p>
        </section>
      </article>
    </div>
  );
}
